// File path: /shell/src/routing/RoleBasedRedirect.tsx
// The "/" route: send a signed-in user to their landing page (D-D, S4 row 8).

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@core/context/AuthContext';
import { useSubscription } from '@core/context/SubscriptionContext';
import { landingPath } from './landingPath';

type Refresh = 'idle' | 'running' | 'done';

const RedirectLoading: React.FC = () => (
  <div className="module-loading" role="status" aria-label="Loading">
    <div className="module-loading-spinner" />
  </div>
);

/**
 * Waits for the subscription context before deciding, because the landing depends on whether
 * the organization has Navigate. After a sign-in in the same tab the context has not loaded the
 * user's modules (it loads on mount, or on a storage event that only other tabs receive), so when
 * loading ends with no modules this asks it to load once, then decides with whatever it has.
 */
const RoleBasedRedirect: React.FC = () => {
  const { user } = useAuth();
  const { isLoading, modules, hasModuleAccess, refreshSubscription } = useSubscription();
  const [refresh, setRefresh] = useState<Refresh>('idle');

  const needsRefresh = !isLoading && modules.length === 0 && refresh === 'idle';

  useEffect(() => {
    if (!needsRefresh) return;
    setRefresh('running');
    // The executor runs now, so the refresh starts in this effect; a synchronous throw becomes
    // a rejection and still ends the wait.
    new Promise<void>((resolve) => resolve(refreshSubscription()))
      .catch((err) => console.warn('Subscription refresh before landing failed:', err))
      .finally(() => setRefresh('done'));
  }, [needsRefresh, refreshSubscription]);

  if (isLoading || needsRefresh || refresh === 'running') {
    return <RedirectLoading />;
  }

  return <Navigate to={landingPath(user?.role, user?.groups, hasModuleAccess)} replace />;
};

export default RoleBasedRedirect;
